import orderDetailsHistoryChangeRideEntryFactory from "./order-details-history-change-ride-entry.factory";
import orderDetailsHistoryOrderRouteHelper from "./order-details-history-order-route.helper";
import OrderDetailsHistoryEntry from "./types/order-details-history-entry";
import OrderDetailsHistoryEntryEvent, {
  OrderDetailsHistoryEntryCancelOrderEvent,
  OrderDetailsHistoryEntryChangeRideEvent,
  OrderDetailsHistoryEntryDealerApprovalEvent,
  OrderDetailsHistoryEntryDispatchApprovalEvent,
  OrderDetailsHistoryEntryInitializeEvent,
  OrderDetailsHistoryEntryMergeOrdersEvent,
  OrderDetailsHistoryEntryOperationalApprovalEvent,
  OrderDetailsHistoryEntryRequestCancelOrderEvent,
  OrderDetailsHistoryEntryInitializeEventRouteWaypoint,
  OrderDetailsHistoryEntryChangeTargetTaxiEvent,
  OrderDetailsHistoryEntryBindPassengerEvent,
  OrderDetailsHistoryEntryExternalIdUpdateEvent,
  OrderDetailsHistoryEntryShuffleMergeEvent,
  OrderDetailsHistoryEventRevertCancelOrderRequestEvent,
  OrderDetailsHistoryEntryChangeApprovalStatusEvent,
  OrderDetailsHistoryEntryChangeApprovalStatusEventApprovalStatus,
} from "./types/order-details-history-entry-event";
import {
  OrderDetailsTHandlingLogEntryForwardEvent,
  OrderDetailsTHandlingLogEntryForwardCancelEvent,
  OrderDetailsTHandlingLogAssignmentOrderNewPlanEvent,
  OrderDetailsTHandlingLogAssignmentOrderExistingPlanEvent,
  OrderDetailsTHandlingLogAssignmentOrderCancelEvent,
} from "./types/order-details-handling-log-entry-event";
import OrderDetailsHistoryEventType from "./types/order-details-history-event-type";
import OrderDetailsHandlingLogEntryEvent from "./types/order-details-handling-log-entry-event";
import {
  OrderCargoOrderDetailsHandlingLogAssignmentOrderNewPlanItem as OrderCargoOrderDetailsHandlingLogAssignmentOrderNewPlanItem,
  OrderCargoOrderDetailsHandlingLogAssignmentOrderExistingPlanItem as OrderCargoOrderDetailsHandlingLogAssignmentOrderExistingPlanItem,
  OrderCargoOrderDetailsHandlingLogCancelItem,
  OrderCargoOrderDetailsHandlingLogAssignmentOrderCancelContentItem as CargoOrderDetailsHandlingLogAssignmentOrderCancelItem,
  OrderCargoOrderDetailsHandlingLogData,
  OrderCargoOrderDetailsHandlingLogItem,
  OrderCargoOrderDetailsHandlingLogType,
} from "../../../../../common/services/order/cargo-order/handling-log/order-cargo-order-details-handling-log";
import OrderCargoOrderHistory, {
  OrderCargoOrderHistoryEventType,
  OrderCargoOrderHistoryItem,
  OrderCargoOrderHistoryItemBindPassengerCommand,
  OrderCargoOrderHistoryItemCancelOrderCommand,
  OrderCargoOrderHistoryItemChangeApprovalStatusCommand,
  OrderCargoOrderHistoryItemChangeApprovalStatusCommandApprovalStatus,
  OrderCargoOrderHistoryItemChangeRideCommand,
  OrderCargoOrderHistoryItemChangeTargetTaxiCommand,
  OrderCargoOrderHistoryItemDealerApprovalCommand,
  OrderCargoOrderHistoryItemDispatchApprovalCommand,
  OrderCargoOrderHistoryItemExternalIdUpdateCommand,
  OrderCargoOrderHistoryItemInitializeCommand,
  OrderCargoOrderHistoryItemInitializeCommandRide,
  OrderCargoOrderHistoryItemMergeOrderCommand,
  OrderCargoOrderHistoryItemOperatorApprovalCommand,
  OrderCargoOrderHistoryItemRequestCancelOrderCommand,
  OrderCargoOrderHistoryItemRevertCancelOrderRequestCommand,
  OrderCargoOrderHistoryItemShuffleMergeCommand,
} from "../../../../../common/services/order/cargo-order/history/order-cargo-order-history";

const createBindPassengerEvent = (
  responsePerformedCommand: OrderCargoOrderHistoryItemBindPassengerCommand,
): OrderDetailsHistoryEntryBindPassengerEvent => {
  return {
    type: OrderDetailsHistoryEventType.BIND_PASSENGER,
  };
};

const createCancelOrderEvent = (
  responsePerformedCommand: OrderCargoOrderHistoryItemCancelOrderCommand,
): OrderDetailsHistoryEntryCancelOrderEvent => {
  return {
    type: OrderDetailsHistoryEventType.CANCEL_ORDER,
  };
};

const createChangeRideEvent = (
  responsePerformedCommand: OrderCargoOrderHistoryItemChangeRideCommand,
): OrderDetailsHistoryEntryChangeRideEvent => {
  const events = orderDetailsHistoryChangeRideEntryFactory.createEvents(
    responsePerformedCommand.updateRideCommandSequence,
  );

  return {
    type: OrderDetailsHistoryEventType.CHANGE_RIDE,
    events,
  };
};

const createChangeTargetTaxiEvent = (
  responsePerformedCommand: OrderCargoOrderHistoryItemChangeTargetTaxiCommand,
): OrderDetailsHistoryEntryChangeTargetTaxiEvent => {
  return {
    type: OrderDetailsHistoryEventType.CHANGE_TARGET_TAXI,
  };
};

const createDealerApprovalEvent = (
  responsePerformedCommand: OrderCargoOrderHistoryItemDealerApprovalCommand,
): OrderDetailsHistoryEntryDealerApprovalEvent => {
  return {
    type: OrderDetailsHistoryEventType.DEALER_APPROVAL,
    approvingDealerUuid: responsePerformedCommand.approvingDealerId,
  };
};

const createDispatchApprovalEvent = (
  responsePerformedCommand: OrderCargoOrderHistoryItemDispatchApprovalCommand,
): OrderDetailsHistoryEntryDispatchApprovalEvent => {
  return {
    type: OrderDetailsHistoryEventType.DISPATCH_APPROVAL,
    approvingDispatcherUuid: responsePerformedCommand.approvingDispatcherId,
  };
};

const createExternalIdUpdateEvent = (
  responsePerformedCommand: OrderCargoOrderHistoryItemExternalIdUpdateCommand,
): OrderDetailsHistoryEntryExternalIdUpdateEvent => {
  return {
    type: OrderDetailsHistoryEventType.EXTERNAL_ID_UPDATE,
    newCargoExternalOrderId: responsePerformedCommand.newExternalId,
  };
};

const createInitializeEventRoute = (
  ride: OrderCargoOrderHistoryItemInitializeCommandRide,
  currentStageNo?: number,
): OrderDetailsHistoryEntryInitializeEventRouteWaypoint[] => {
  const route: OrderDetailsHistoryEntryInitializeEventRouteWaypoint[] = [];

  let stageNo = currentStageNo ?? 1;

  ride.seq.forEach((item) => {
    const isRideWaypoint =
      orderDetailsHistoryOrderRouteHelper.checkIsResponseRideSeqItemAWaypoint(
        item,
      );
    if (isRideWaypoint) {
      route.push({
        addressDisplayName: item.displayName,
        haltingTime: item.haltingTime,
        plannedDate: item.time ? new Date(item.time) : null,
        stageNo,
      });

      stageNo++;
      return;
    }

    const groupRouteWaypoints = createInitializeEventRoute(item, stageNo);

    route.push(...groupRouteWaypoints);

    stageNo++;
  });

  return route;
};

const createInitializeEvent = (
  responseItem: OrderCargoOrderHistoryItem,
): OrderDetailsHistoryEntryInitializeEvent => {
  const responsePerformedCommand =
    responseItem.performedCommand as OrderCargoOrderHistoryItemInitializeCommand;

  const route = createInitializeEventRoute(responsePerformedCommand.ride);
  return {
    type: OrderDetailsHistoryEventType.INITIALIZE,
    cargoExternalOrderId: responsePerformedCommand.externalId,
    contractModel: responseItem.cargoTaxiBillingModel ?? null,
    taxiCorporationName: responseItem.taxiCorporationDisplayName,
    route,
  };
};

const createMergeOrderEvent = (
  responsePerformedCommand: OrderCargoOrderHistoryItemMergeOrderCommand,
): OrderDetailsHistoryEntryMergeOrdersEvent => {
  return {
    type: OrderDetailsHistoryEventType.MERGE_ORDERS,
  };
};

const createOperatorApprovalEvent = (
  responsePerformedCommand: OrderCargoOrderHistoryItemOperatorApprovalCommand,
): OrderDetailsHistoryEntryOperationalApprovalEvent => {
  return {
    type: OrderDetailsHistoryEventType.OPERATIONAL_APPROVAL,
    operatorUuid: responsePerformedCommand.approvingOperatorId,
  };
};

const createRequestCancelOrderEvent = (
  responsePerformedCommand: OrderCargoOrderHistoryItemRequestCancelOrderCommand,
): OrderDetailsHistoryEntryRequestCancelOrderEvent => {
  return {
    type: OrderDetailsHistoryEventType.REQUEST_CANCEL_ORDER,
  };
};

const createRevertCancelOrderRequestEvent = (
  responsePerformedCommand: OrderCargoOrderHistoryItemRevertCancelOrderRequestCommand,
): OrderDetailsHistoryEventRevertCancelOrderRequestEvent => {
  return {
    type: OrderDetailsHistoryEventType.REVERT_CANCEL_ORDER_REQUEST,
  };
};

const createShuffleMergeCommand = (
  responsePerformedCommand: OrderCargoOrderHistoryItemShuffleMergeCommand,
): OrderDetailsHistoryEntryShuffleMergeEvent => {
  return {
    type: OrderDetailsHistoryEventType.SHUFFLE_MERGE,
    sourceInternalOrderIds: responsePerformedCommand.representation.sources.map(
      (data) => data.humanId,
    ),
    targetInternalOrderId:
      responsePerformedCommand.representation.target.humanId,
  };
};

const createApprovalStatus = (
  status: OrderCargoOrderHistoryItemChangeApprovalStatusCommandApprovalStatus,
): OrderDetailsHistoryEntryChangeApprovalStatusEventApprovalStatus => {
  switch (status) {
    case OrderCargoOrderHistoryItemChangeApprovalStatusCommandApprovalStatus.ACCEPTED:
      return OrderDetailsHistoryEntryChangeApprovalStatusEventApprovalStatus.ACCEPTED;
    case OrderCargoOrderHistoryItemChangeApprovalStatusCommandApprovalStatus.REJECTED:
      return OrderDetailsHistoryEntryChangeApprovalStatusEventApprovalStatus.REJECTED;
  }
};

const createChangeApprovalStatusCommand = (
  responsePerformedCommand: OrderCargoOrderHistoryItemChangeApprovalStatusCommand,
): OrderDetailsHistoryEntryChangeApprovalStatusEvent => {
  return {
    type: OrderDetailsHistoryEventType.CHANGE_APPROVAL_STATUS,
    approvalStatus: createApprovalStatus(responsePerformedCommand.newStatus),
  };
};

const createHistoryEvent = (
  responseItem: OrderCargoOrderHistoryItem,
): OrderDetailsHistoryEntryEvent => {
  switch (responseItem.performedCommand.commandType) {
    case OrderCargoOrderHistoryEventType.BIND_PASSENGER:
      return createBindPassengerEvent(
        responseItem.performedCommand as OrderCargoOrderHistoryItemBindPassengerCommand,
      );
    case OrderCargoOrderHistoryEventType.CANCEL_ORDER:
      return createCancelOrderEvent(
        responseItem.performedCommand as OrderCargoOrderHistoryItemCancelOrderCommand,
      );
    case OrderCargoOrderHistoryEventType.CHANGE_RIDE:
      return createChangeRideEvent(
        responseItem.performedCommand as OrderCargoOrderHistoryItemChangeRideCommand,
      );
    case OrderCargoOrderHistoryEventType.CHANGE_TARGET_TAXI:
      return createChangeTargetTaxiEvent(
        responseItem.performedCommand as OrderCargoOrderHistoryItemChangeTargetTaxiCommand,
      );
    case OrderCargoOrderHistoryEventType.DEALER_APPROVAL:
      return createDealerApprovalEvent(
        responseItem.performedCommand as OrderCargoOrderHistoryItemDealerApprovalCommand,
      );
    case OrderCargoOrderHistoryEventType.DISPATCH_APPROVAL:
      return createDispatchApprovalEvent(
        responseItem.performedCommand as OrderCargoOrderHistoryItemDispatchApprovalCommand,
      );
    case OrderCargoOrderHistoryEventType.EXTERNAL_ID_UPDATE:
      return createExternalIdUpdateEvent(
        responseItem.performedCommand as OrderCargoOrderHistoryItemExternalIdUpdateCommand,
      );
    case OrderCargoOrderHistoryEventType.INITIALIZE:
      return createInitializeEvent(responseItem);
    case OrderCargoOrderHistoryEventType.MERGE_ORDERS:
      return createMergeOrderEvent(
        responseItem.performedCommand as OrderCargoOrderHistoryItemMergeOrderCommand,
      );
    case OrderCargoOrderHistoryEventType.OPERATIONAL_APPROVAL:
      return createOperatorApprovalEvent(
        responseItem.performedCommand as OrderCargoOrderHistoryItemOperatorApprovalCommand,
      );
    case OrderCargoOrderHistoryEventType.REQUEST_CANCEL_ORDER:
      return createRequestCancelOrderEvent(
        responseItem.performedCommand as OrderCargoOrderHistoryItemRequestCancelOrderCommand,
      );
    case OrderCargoOrderHistoryEventType.REVERT_CANCEL_ORDER_REQUEST:
      return createRevertCancelOrderRequestEvent(
        responseItem.performedCommand as OrderCargoOrderHistoryItemRevertCancelOrderRequestCommand,
      );
    case OrderCargoOrderHistoryEventType.SHUFFLE_MERGE:
      return createShuffleMergeCommand(
        responseItem.performedCommand as OrderCargoOrderHistoryItemShuffleMergeCommand,
      );
    case OrderCargoOrderHistoryEventType.CHANGE_APPROVAL_STATUS:
      return createChangeApprovalStatusCommand(
        responseItem.performedCommand as OrderCargoOrderHistoryItemChangeApprovalStatusCommand,
      );
    default:
      return undefined;
  }
};

const createForwardingOrderEvent = (
  item: OrderCargoOrderDetailsHandlingLogItem,
): OrderDetailsTHandlingLogEntryForwardEvent => {
  return {
    type: OrderCargoOrderDetailsHandlingLogType.FORWARD,
    contractDisplayName: item.contractDisplayName,
  };
};

const createForwardingCancelOrderEvent = (
  item: OrderCargoOrderDetailsHandlingLogCancelItem,
): OrderDetailsTHandlingLogEntryForwardCancelEvent => {
  return {
    type: OrderCargoOrderDetailsHandlingLogType.FORWARD_CANCEL,
    producerDisplayName: item.producerDisplayName,
    consumerDisplayName: item.consumerDisplayName,
  };
};

const createAssignmentOrderNewPlanEvent = (
  item: OrderCargoOrderDetailsHandlingLogAssignmentOrderNewPlanItem,
): OrderDetailsTHandlingLogAssignmentOrderNewPlanEvent => {
  return {
    type: OrderCargoOrderDetailsHandlingLogType.ACCEPTED_CPE_CANDIDATURE,
    driverName: item.driverName,
    taxiCorporationName: item.taxiCorporationName,
    createDate: item.createDate,
    author: {
      firstName: item.author.firstName,
      lastName: item.author.lastName,
    },
  };
};

const createAssignmentOrderExistingPlanEvent = (
  item: OrderCargoOrderDetailsHandlingLogAssignmentOrderExistingPlanItem,
): OrderDetailsTHandlingLogAssignmentOrderExistingPlanEvent => {
  return {
    type: OrderCargoOrderDetailsHandlingLogType.ACCEPTED_CSO_CANDIDATURE,
    driverName: item.driverName,
    taxiCorporationName: item.taxiCorporationName,
    createDate: item.createDate,
    author: {
      firstName: item.author.firstName,
      lastName: item.author.lastName,
    },
    planEntryUuid: item.planEntryUuid,
  };
};

const createAssignmentOrderCancelEvent = (
  item: CargoOrderDetailsHandlingLogAssignmentOrderCancelItem,
): OrderDetailsTHandlingLogAssignmentOrderCancelEvent => {
  return {
    type: OrderCargoOrderDetailsHandlingLogType.ACCEPTED_DSO_CANDIDATURE,
    createDate: item.createDate,
    author: {
      firstName: item.author.firstName,
      lastName: item.author.lastName,
    },
    planEntryUuid: item.planEntryUuid,
  };
};

const createHandlingLogEvent = (
  item: OrderCargoOrderDetailsHandlingLogData,
): OrderDetailsHandlingLogEntryEvent => {
  switch (item.type) {
    case OrderCargoOrderDetailsHandlingLogType.FORWARD:
      return createForwardingOrderEvent(
        item as OrderCargoOrderDetailsHandlingLogItem,
      );
    case OrderCargoOrderDetailsHandlingLogType.FORWARD_CANCEL:
      return createForwardingCancelOrderEvent(
        item as OrderCargoOrderDetailsHandlingLogCancelItem,
      );
    case OrderCargoOrderDetailsHandlingLogType.ACCEPTED_CPE_CANDIDATURE:
      return createAssignmentOrderNewPlanEvent(
        item as OrderCargoOrderDetailsHandlingLogAssignmentOrderNewPlanItem,
      );
    case OrderCargoOrderDetailsHandlingLogType.ACCEPTED_CSO_CANDIDATURE:
      return createAssignmentOrderExistingPlanEvent(
        item as OrderCargoOrderDetailsHandlingLogAssignmentOrderExistingPlanItem,
      );
    case OrderCargoOrderDetailsHandlingLogType.ACCEPTED_DSO_CANDIDATURE:
      return createAssignmentOrderCancelEvent(
        item as CargoOrderDetailsHandlingLogAssignmentOrderCancelItem,
      );
    default:
      return undefined;
  }
};

const createHistoryEntry = (
  responseItem: OrderCargoOrderHistoryItem,
): OrderDetailsHistoryEntry => {
  const event = createHistoryEvent(responseItem);
  return {
    authorDisplayName: `${responseItem.author.firstName} ${responseItem.author.lastName}`,
    date: new Date(responseItem.createdAt),
    event,
  };
};

const createHandlingLogDataEntries = (
  item: OrderCargoOrderDetailsHandlingLogData,
): OrderDetailsHistoryEntry => {
  const event = createHandlingLogEvent(item);
  return {
    authorDisplayName: `${item.author.firstName} ${item.author.lastName}`,
    date: item.createDate,
    event,
  };
};

const createHistoryEntries = (
  historyData: OrderCargoOrderHistory,
  handlingLogData: OrderCargoOrderDetailsHandlingLogData[],
): OrderDetailsHistoryEntry[] => {
  const historyEntries = historyData.data.map(createHistoryEntry);
  const handlingLogEntries = handlingLogData
    ? handlingLogData.map(createHandlingLogDataEntries)
    : [];

  return [...historyEntries, ...handlingLogEntries].sort(
    (entryA, entryB) => entryB.date.getTime() - entryA.date.getTime(),
  );
};

const orderDetailsHistoryEntryFactory = {
  createHistoryEntries,
};

export default orderDetailsHistoryEntryFactory;
